import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Loader2, Save, Info, AlertCircle, CheckCircle2, UploadCloud } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Card } from "@cms/ui/components/card";
import { Textarea } from "@cms/ui/components/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@cms/ui/components/tabs";
import { Alert, AlertDescription } from "@cms/ui/components/alert";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@cms/ui/components/alert-dialog";
import { PRIMARY_BRAND_SLOT, paletteFromTokens, tokensWithPalette } from "@cms/portal-lib/palette";
import type { ApiClient } from "../lib/api";
import {
  A11Y_TARGET_TOKEN,
  TOKEN_GROUPS,
  TOKEN_DEFAULTS,
  parseA11yTarget,
  type A11yTarget,
} from "../lib/design-tokens";
import {
  buildPersistedTokens,
  previewTokens,
  resolveTypographyScale,
  sanitizeLoadedOverrides,
} from "../lib/branding-state";
import type { AdminClient } from "../lib/outlet-context";
import { PaletteEditor } from "./palette-editor";
import { TokenGroupGrid } from "./token-group-grid";
import { BrandingPreview } from "./branding-preview";
import { TypographyPreviewModal } from "./typography-preview-modal";
import { TokenContrastChecks } from "./token-contrast-checks";
import { useGoogleFontsPreview } from "../hooks/use-google-fonts-preview";

// Admin acts on the client's own site_config via the act-as header.
const actAsHeaders = (tenantId: string) => ({ "x-act-as-tenant": tenantId });

function parseOverrides(raw: unknown): Record<string, string> {
  if (!raw) return {};
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, string>)
      : {};
  } catch {
    return {};
  }
}

/** AA / AAA choice controlling every calculated colour. */
function A11yTargetPicker({ target, onChange }: {
  target: A11yTarget;
  onChange: (target: A11yTarget) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="inline-flex rounded-md border p-0.5" role="radiogroup" aria-label="Accessibility target">
        {(["aa", "aaa"] as const).map((level) => (
          <button
            key={level}
            type="button"
            role="radio"
            aria-checked={target === level}
            onClick={() => onChange(level)}
            className={`rounded px-3 py-1 text-xs font-semibold ${
              target === level ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
            }`}
          >
            {level.toUpperCase()}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {target === "aaa"
          ? "AAA — calculated colours aim for at least 7:1 contrast."
          : "AA — calculated colours aim for at least 4.5:1 contrast."}
      </p>
    </div>
  );
}

/** Explains why the calculated text colours (and their ratios) shift with the target. */
function CalculatedColoursNote() {
  return (
    <div className="flex gap-2 rounded-md border border-dashed bg-muted/40 p-2.5 text-xs text-muted-foreground">
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div className="space-y-1">
        <p>
          Body, muted, header, footer and hero text — plus each palette colour's readable
          text — are <span className="font-medium text-foreground">calculated</span>, not picked.
          They're derived from your brand colours to meet the target ratio (AA ≥ 4.5:1, AAA ≥ 7:1).
        </p>
        <p>
          Raising AA → AAA re-derives those text colours darker/purer to clear the higher bar,
          so the ratios on the palette chips and in Contrast checks change. Colours you set
          directly (accent, backgrounds) are never altered — only their pass/fail verdict moves.
        </p>
      </div>
    </div>
  );
}

interface Notice {
  ok: boolean;
  text: string;
}

export function BrandingPanel({ client, api }: {
  client: AdminClient;
  api: ApiClient | null;
}) {
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [tab, setTab] = useState("editor");
  const [rawText, setRawText] = useState("{}");
  const [rawError, setRawError] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api.request<{ config?: { design_tokens?: unknown } }>(
      "/tenant/config",
      { headers: actAsHeaders(client.id) }
    )
      .then((data) => {
        if (cancelled) return;
        setOverrides(sanitizeLoadedOverrides(parseOverrides(data.config?.design_tokens)));
        setLoaded(true);
      })
      .catch((err: Error) => !cancelled && setLoadError(err.message));
    return () => {
      cancelled = true;
    };
  }, [api, client.id]);

  const target = parseA11yTarget(overrides[A11Y_TARGET_TOKEN]);
  const palette = useMemo(() => paletteFromTokens(overrides), [overrides]);
  const palettePresets = useMemo(() => palette.filter(Boolean).map((value) => ({ value })), [palette]);
  // Editable values + inherited brand colours + calculated tokens.
  const merged = useMemo(() => previewTokens(overrides, target), [overrides, target]);
  // Placeholders show what a field inherits: accent falls back to the Primary
  // Brand Colour, and the maximum type scale to the minimum one, not the
  // theme defaults.
  const fieldDefaults = useMemo(
    () => ({
      ...TOKEN_DEFAULTS,
      accent: palette[PRIMARY_BRAND_SLOT]?.trim() || TOKEN_DEFAULTS.accent,
      "typography-scale-max": resolveTypographyScale(overrides).toFixed(3),
    }),
    [palette, overrides]
  );

  // Load Google Fonts CSS so the live preview renders the selected typefaces.
  useGoogleFontsPreview(overrides);

  const setPalette = useCallback((colors: string[]) => {
    setOverrides((prev) => tokensWithPalette(prev, colors));
  }, []);

  const setToken = useCallback((key: string, value: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (value === "" || value == null) delete next[key];
      else next[key] = value;
      return next;
    });
  }, []);

  const setTarget = useCallback((next: A11yTarget) => setToken(A11Y_TARGET_TOKEN, next), [setToken]);

  const onTabChange = (next: string) => {
    // Seed the raw editor from the current overrides when entering the Raw tab.
    if (next === "raw") {
      setRawText(JSON.stringify(overrides, null, 2));
      setRawError("");
    }
    setTab(next);
  };

  const onRawChange = (text: string) => {
    setRawText(text);
    try {
      const parsed = JSON.parse(text || "{}");
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setRawError('Must be a JSON object, e.g. { "accent": "#b45309" }');
        return;
      }
      setOverrides(parsed);
      setRawError("");
    } catch (err) {
      setRawError(`Invalid JSON: ${(err as Error).message}`);
    }
  };

  const save = async () => {
    if (rawError || !api) return;
    setSaving(true);
    setNotice(null);
    try {
      // Persist deltas + palette + target + calculated tokens, all as oklch().
      const persisted = buildPersistedTokens(overrides, target);
      await api.request("/tenant/config", {
        method: "PUT",
        headers: actAsHeaders(client.id),
        body: JSON.stringify({ design_tokens: JSON.stringify(persisted) }),
      });
      setOverrides(sanitizeLoadedOverrides(persisted));
      setNotice({
        ok: true,
        text: "Branding saved. Click Publish to rebuild the client's site.",
      });
    } catch (err) {
      setNotice({ ok: false, text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  // Staged publish: saving branding no longer rebuilds. The admin triggers the
  // single build here, acting as the client.
  const publish = async () => {
    if (!api) return;
    setPublishing(true);
    setNotice(null);
    try {
      await api.request("/tenant/publish", {
        method: "POST",
        headers: actAsHeaders(client.id),
      });
      setNotice({ ok: true, text: "Publishing — the client's site is rebuilding (~2 minutes)." });
    } catch (err) {
      setNotice({ ok: false, text: (err as Error).message });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Heading left, actions right — above the section boxes. */}
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Branding</h2>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving || !loaded || !!rawError}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" disabled={publishing || !loaded}>
                {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
                Publish
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Publish {client.business_name || "this client"}'s site?</AlertDialogTitle>
                <AlertDialogDescription>
                  This rebuilds the client's live site with every saved change — branding,
                  settings and pages. It takes about two minutes. Save your branding edits first.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={publish}>Publish now</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {loadError ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      ) : !loaded ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {notice && (
            <Alert variant={notice.ok ? "default" : "destructive"}>
              {notice.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
              <AlertDescription>{notice.text}</AlertDescription>
            </Alert>
          )}

          {/* Left: the three editing boxes. Right: live preview + contrast checks. */}
          <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
            <div className="space-y-4">
              <Card className="p-4" aria-label="Accessibility target">
                <h3 className="text-sm font-semibold">Accessibility target</h3>
                <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
                  Text colours, hover shades and borders are calculated to meet this WCAG level.
                </p>
                <A11yTargetPicker target={target} onChange={setTarget} />
                <div className="mt-3">
                  <CalculatedColoursNote />
                </div>
              </Card>

              <Card className="p-4" aria-label="Brand palette">
                <h3 className="text-sm font-semibold">Brand palette</h3>
                <p className="mb-2.5 mt-0.5 text-xs text-muted-foreground">
                  All colours are stored as oklch — hex, rgb() and hsl() values are converted on save.
                </p>
                <PaletteEditor palette={palette} target={target} onChange={setPalette} />
              </Card>

              <Card className="p-4" aria-label="Design tokens">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-sm font-semibold">Design tokens</h3>
                    <p className="mb-2.5 mt-0.5 text-xs text-muted-foreground">
                      Fine-tune individual tokens by area. Calculated colours (text, hover shades,
                      borders) are layered on automatically and shift with the accessibility target.
                    </p>
                  </div>
                  <TypographyPreviewModal merged={merged} />
                </div>
                <Tabs value={tab} onValueChange={onTabChange}>
                  <TabsList>
                    <TabsTrigger value="editor">Editor</TabsTrigger>
                    <TabsTrigger value="raw">Raw</TabsTrigger>
                  </TabsList>

                  <TabsContent value="editor">
                    <TokenGroupGrid
                      groups={TOKEN_GROUPS}
                      overrides={overrides}
                      defaults={fieldDefaults}
                      presets={palettePresets}
                      setToken={setToken}
                    />
                  </TabsContent>

                  <TabsContent value="raw">
                    <p className="mb-2 text-xs text-muted-foreground">
                      The raw override object — only the tokens you change, keyed without the
                      leading <code className="mx-1 rounded bg-muted px-1">--</code>. Calculated
                      tokens (text colours, hover shades, borders) are added on save.
                    </p>
                    <Textarea
                      value={rawText}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onRawChange(e.target.value)}
                      spellCheck={false}
                      className="min-h-[320px] font-mono text-xs"
                    />
                    {rawError && (
                      <p className="mt-2 flex items-center gap-1 text-xs text-destructive">
                        <AlertCircle className="h-3.5 w-3.5" />
                        {rawError}
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              </Card>
            </div>

            <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
              <Card className="p-4" aria-label="Live preview">
                <h3 className="mb-2 text-sm font-semibold">Live preview</h3>
                <BrandingPreview merged={merged} />
              </Card>
              <Card className="p-4" aria-label="Contrast checks">
                <h3 className="mb-2 text-sm font-semibold">Contrast checks</h3>
                <TokenContrastChecks merged={merged} target={target} />
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
